import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { LogOut, Building2, Bell, Clock, Trash2, Gift, RefreshCw, Package, Menu, X } from 'lucide-react'; // ✅ Added Menu, X
import axios from 'axios';
import logo from '../unify_logo.png';

const Layout = ({ onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user')) || { name: 'Admin' };
  const isActive = (path) => location.pathname === path || location.pathname.includes(path);

  const [notifications, setNotifications] = useState([]);
  const [showNotif, setShowNotif] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false); // ✅ Mobile Sidebar State
  const notifRef = useRef(null);

  // Change this to match your local folder path
  const API_URL = 'http://localhost:5000/api';
  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API_URL}/notifications/unseen?_t=${Date.now()}`);
      if (Array.isArray(res.data)) setNotifications(res.data);
    } catch (error) { console.error("Error fetching notifications", error); }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (notifRef.current && !notifRef.current.contains(event.target)) setShowNotif(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [notifRef]);

  const handleNotificationClick = async (rxId, patientId, type) => {
    try {
      if (type !== 'Birthday') await axios.post(`${API_URL}/notifications/seen`, { rx_id: rxId });
      setNotifications(prev => prev.filter(n => n.rx_id !== rxId));
      setShowNotif(false);
      navigate(`/patient/${patientId}`);
    } catch (error) { navigate(`/patient/${patientId}`); }
  };

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">

      {/* ✅ MOBILE SIDEBAR OVERLAY */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* ✅ RESPONSIVE SIDEBAR */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-white border-r shadow-lg transform transition-transform duration-300 ease-in-out
        lg:translate-x-0 lg:static lg:shadow-sm
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="p-5 flex flex-col justify-center items-center border-b border-gray-100 relative">
          {/* Close button for mobile */}
          <button onClick={() => setSidebarOpen(false)} className="absolute top-4 right-4 lg:hidden text-gray-500">
            <X size={24} />
          </button>

          <img src={logo} alt="UNiFy Rx" className="h-14 w-auto object-contain mb-2" />
          <div className="text-center">
            <div className="text-lg font-extrabold text-teal-700 tracking-tight leading-none">UNiFy Rx</div>
            <span className="text-[9px] font-bold text-green-600 uppercase tracking-widest">CRM Suite</span>
          </div>
        </div>

        <nav className="mt-6 px-4">
          <p className="text-[10px] font-bold text-gray-400 uppercase mb-2 px-2">Select Pharmacy</p>
          <Link to="/patients" onClick={() => setSidebarOpen(false)} className={`flex items-center gap-3 px-4 py-3 rounded-lg transition font-medium ${location.pathname !== '/' && location.pathname !== '/recycle-bin' ? 'bg-teal-50 text-teal-700 border-r-4 border-teal-600' : 'text-gray-600 hover:bg-gray-50'}`}>
            <Building2 size={20} /> A & G Pharmacy
          </Link>
        </nav>

        <div className="mt-auto p-4 border-t border-gray-100">
          <Link to="/recycle-bin" onClick={() => setSidebarOpen(false)} className={`flex items-center gap-3 px-4 py-3 rounded-lg transition font-medium mb-2 ${isActive('/recycle-bin') ? 'bg-red-50 text-red-600' : 'text-gray-600 hover:bg-gray-50'}`}>
            <Trash2 size={20} /> Recycle Bin
          </Link>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <div className="flex-1 flex flex-col overflow-hidden w-full">

        {/* HEADER */}
        <header className="bg-white border-b h-16 flex items-center justify-between px-4 lg:px-8 shadow-sm relative z-20 shrink-0">

          <div className="flex items-center gap-3">
            {/* ✅ HAMBURGER MENU (Visible only on Mobile) */}
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-gray-600">
              <Menu size={24} />
            </button>

            {/* ✅ NAVIGATION (Hidden on Mobile, Visible on Desktop) */}
            <div className="hidden lg:flex space-x-6 text-gray-600 font-medium text-sm">
              <Link to="/" className={`hover:text-teal-600 transition ${location.pathname === '/' ? 'text-teal-600 font-bold' : ''}`}>Home</Link>
              <Link to="/patients" className={`hover:text-teal-600 transition ${isActive('/patients') ? 'text-teal-600 font-bold' : ''}`}>Patients</Link>
              <Link to="/doctors" className={`hover:text-teal-600 transition ${isActive('/doctors') ? 'text-teal-600 font-bold' : ''}`}>Doctors</Link>
              <Link to="/refills" className={`hover:text-teal-600 transition ${isActive('/refills') ? 'text-teal-600 font-bold' : ''}`}>Refills</Link>
              <Link to="/history" className={`hover:text-teal-600 transition ${isActive('/history') ? 'text-teal-600 font-bold' : ''}`}>History</Link>
              <Link to="/tracker" className={`hover:text-teal-600 transition ${isActive('/tracker') ? 'text-teal-600 font-bold' : ''}`}>Tracker</Link>
              <Link to="/priority" className={`hover:text-teal-600 transition ${isActive('/priority') ? 'text-red-600 font-bold' : ''}`}>Priority</Link>
              <Link to="/logs" className={`hover:text-teal-600 transition ${isActive('/logs') ? 'text-teal-600 font-bold' : ''}`}>Logs</Link>
            </div>
            {/* Mobile Title (When nav is hidden) */}
            <span className="lg:hidden font-bold text-teal-700">UNiFy Rx</span>
          </div>

          {/* Right: Notifications, User */}
          <div className="flex items-center gap-3">
            <div className="relative" ref={notifRef}>
              <button onClick={() => setShowNotif(!showNotif)} className="relative p-2 text-gray-500 hover:bg-gray-100 rounded-full transition">
                <Bell size={20} />
                {notifications.length > 0 && (<span className="absolute top-1 right-1 h-4 w-4 bg-red-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full border border-white">{notifications.length}</span>)}
              </button>
              {showNotif && (
                <div className="absolute right-0 mt-2 w-72 sm:w-80 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50">
                  <div className="p-3 border-b bg-gray-50 flex justify-between items-center"><h3 className="font-bold text-gray-700 text-sm">Notifications</h3><span className="text-xs text-gray-500">{notifications.length} Alerts</span></div>
                  <div className="max-h-80 overflow-y-auto custom-scrollbar">
                    {notifications.length === 0 ? (<div className="p-8 text-center text-gray-400 text-sm">No new alerts.</div>) : (
                      notifications.map((n, i) => (
                        <div key={i} onClick={() => handleNotificationClick(n.rx_id, n.patient_id, n.type)} className="p-3 border-b border-gray-50 hover:bg-blue-50 transition flex items-start gap-3 cursor-pointer">
                          <div className="mt-1">{n.type === 'Expiration' && <Clock size={16} className="text-orange-500" />}{n.type === 'Pickup' && <Package size={16} className="text-red-600" />}{n.type === 'Refill' && <RefreshCw size={16} className="text-teal-500" />}{n.type === 'Birthday' && <Gift size={16} className="text-pink-500" />}</div>
                          <div><p className="text-sm font-bold text-gray-800">{n.drug_name}</p><p className="text-xs text-gray-500">{n.full_name}</p></div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="h-8 w-px bg-gray-300 hidden sm:block"></div>

            <div className="text-right hidden md:block">
              <p className="text-sm font-bold text-gray-800">{user.name}</p>
              <p className="text-[10px] text-teal-600 font-bold uppercase">{user.user_code || 'N/A'}</p>
            </div>

            <div className="h-9 w-9 bg-teal-100 rounded-full flex items-center justify-center text-teal-700 font-bold text-sm">
              {user.name.charAt(0)}
            </div>

            <button onClick={onLogout} className="flex items-center gap-2 text-gray-500 hover:text-red-600 transition ml-1">
              <LogOut size={18} />
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-4 lg:p-8 w-full">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;